import {
    Box,
    Collapse,
    Divider,
    Icon,
    Slider,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { ChangeEvent, useEffect, useState } from "react";
import {
    rangeSelectLabelFormatter,
    rangeSelectValueProvider,
} from "../../constants/ProjectConstants";

// updated
export default function RangeSelect(props: {
    label: string;
    from?: number;
    to?: number;
    valueProvider: rangeSelectValueProvider;
    onValueChange: (min: number, max: number) => void;
}) {
    const [open, setOpen] = useState(false);

    const [from, setFrom] = useState<number | null>(
        props.from ?? props.valueProvider.min
    );
    const [to, setTo] = useState<number | null>(
        props.to ?? props.valueProvider.max
    );

    useEffect(() => {
        setFrom(props.from ?? props.valueProvider.min);
        setTo(props.to ?? props.valueProvider.max);
    }, [props.from, props.to]);

    const toggleOpen = () => {
        setOpen(!open);
    };

    const onDragEnded = () => {
        let valueFrom = from;
        let valueTo = to;

        if (from === props.valueProvider.min) {
            valueFrom = null;
        }

        if (to === props.valueProvider.max) {
            valueTo = null;
        }

        props.onValueChange(valueFrom, valueTo);
    };

    const handleFromChange = (e: ChangeEvent<HTMLInputElement>) => {
        let fromValue = parseInt(e.target.value);

        if (isNaN(fromValue)) {
            fromValue = null;
        }

        props.onValueChange(fromValue, props.to);
    };

    const handleToChange = (e: ChangeEvent<HTMLInputElement>) => {
        let toValue = parseInt(e.target.value);

        if (isNaN(toValue)) {
            toValue = null;
        }

        props.onValueChange(props.from, toValue);
    };

    return (
        <Box sx={{ width: "100%", px: 1, boxSizing: "border-box" }}>
            <Stack
                direction="row"
                alignItems="center"
                sx={{ cursor: "pointer", mb: 1 }}
                onClick={toggleOpen}
            >
                <Typography>{props.label}</Typography>

                <Box flexGrow={1} />

                <Icon color="action">
                    {open ? "expand_less" : "expand_more"}
                </Icon>
            </Stack>

            <Box sx={{ px: 1 }}>
                <Slider
                    value={[from, to]}
                    disableSwap
                    min={props.valueProvider.min}
                    max={props.valueProvider.max}
                    step={props.valueProvider.step}
                    marks={props.valueProvider.marks?.map((i) => {
                        return {
                            value: i,
                        };
                    })}
                    onChangeCommitted={onDragEnded}
                    onChange={(event: Event, newValue: number[]) => {
                        let from: number | null = newValue[0];
                        let to: number | null = newValue[1];

                        // enable min distance between handlers
                        if (newValue[1] - newValue[0] < 1) {
                            return;
                        }

                        setFrom(from);
                        setTo(to);
                    }}
                    valueLabelDisplay="auto"
                    valueLabelFormat={rangeSelectLabelFormatter(
                        props.valueProvider
                    )}
                />
            </Box>

            <Collapse in={open}>
                <Divider sx={{ mt: 1, mb: 2 }} />

                <Stack>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography fontSize="smaller" color="text.secondary">
                            از
                        </Typography>
                        <TextField
                            size="small"
                            fullWidth
                            onChange={handleFromChange}
                            value={props.from ?? ""}
                        />
                    </Stack>

                    <Icon sx={{ width: 10 }} fontSize="small" color="disabled">
                        more_vert
                    </Icon>

                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography fontSize="smaller" color="text.secondary">
                            تا
                        </Typography>
                        <TextField
                            size="small"
                            fullWidth
                            onChange={handleToChange}
                            value={props.to ?? ""}
                        />
                    </Stack>
                </Stack>

                <Divider sx={{ mt: 2, mb: 1 }} />
            </Collapse>

            {(props.from || props.to) && (
                <Collapse in={!open}>
                    <Stack direction="row" spacing={0.5}>
                        <Typography color="text.secondary">از</Typography>
                        <Typography color="primary">
                            {props.from ?? props.valueProvider.min}
                        </Typography>
                        <Typography color="text.secondary">
                            {props.valueProvider.unit}
                        </Typography>

                        <Typography color="text.secondary">تا</Typography>
                        <Typography color="primary">
                            {props.to ?? props.valueProvider.max}
                        </Typography>
                        <Typography color="text.secondary">
                            {props.valueProvider.unit}
                        </Typography>
                    </Stack>
                </Collapse>
            )}
        </Box>
    );
}
